import React, { useEffect, useMemo, useState, useRef } from "react";
import { useTable, useSortBy, usePagination, useRowSelect } from "react-table";
import "tailwindcss/tailwind.css";
import { Empty } from "antd";
import "../../styles.css";
import Vectorsorting from "../../assets/Vectorsorting.svg";
import ShimmerLoader from "../../loading";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router-dom";
import CustomPagination from "../../components/CustomWidgets/CustomPagination";
import { Tooltip } from "antd";
import { useSelector } from "react-redux";
import { routes } from "../../Routes";
import i18next from "i18next";
import {
  getPurchaseList,
  getVendorDetails,
  getVendorInventoryDetails,
  getVendorProducts,
} from "../../API  Functions/PurchaseFunctions";



const getStartOfMonth = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}-01`;
};

const getCurrentDate = () => {
  const today = new Date();
  return today.toISOString().split("T")[0];
};

// Helper: map paymentType number → state param string (mirror your existing logic)
const toStateParam = (paymentType) => {
  switch (paymentType) {
    case 1: return "purchase";
    case 2: return "done";
    case 3: return "cancel";
    default: return "";
  }
};

// Helper: flatten a purchase row (mirror your existing flattenRow logic)
const flattenRow = (val) => {
  const od = val?.order_details ?? {};
  return {
    // ── original vendor-payment fields ──────────────────────────────────────
    order_no: od.order_no ?? "",
    supplier_name: od.partner_name ?? "",
    supplier_id: od.partner_id ?? null,
    order_date: od.order_date ?? "",
    vendor_reference: od.venfor_ref ?? "",
    invoice_value: od.total_amount ?? 0,
    paid_amount: od.paid_amount ?? 0,
    due_amount: od.due_amount ?? 0,
    store_name: od.store_name ?? "",
    bill_id: od.bill_id ?? null,
    // ── new fields from purchase history ────────────────────────────────────
    status: od.status ?? "",
    payment_status: od.payment_status ?? "",
    create_date: od.create_date ?? "",
    due_date: od.due_date ?? "",
    payment_term: od.payment_term ?? "",
    quantity: val.lines?.reduce((s, l) => s + (l.product_qty ?? 0), 0) ?? 0,
    // ── raw original kept so row-click can pass full orderData ───────────────
    _raw: val,
  };
};



const ExpenseManagementTable = ({ additionalData, pagination }) => {
  const { t, i18n } = useTranslation();
  const [flag, setflag] = useState(false);
  const navigate = useNavigate();
  const allStores = useSelector(
    (state) => state?.storeReducer?.selectedStore?.is_select_all_store,
  );
  const company_id = useSelector(
    (state) => state?.login?.user?.result?.company_id,
  );
  const user_id = useSelector(
    (state) => state?.login?.user?.result?.user_id,
  );

  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(true);
  const [searchByShortCode, setSearchByShortCode] = useState(false);
  const [searchid, setSearchId] = useState(localStorage.getItem("searchid"));
  const [vendorData, setVendorData] = useState({});
  const [vendorInventoryData, setVendorInventoryData] = useState({});

  // Products section state
  const [productActiveTab, setProductActiveTab] = useState("orders");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRange, setSelectedRange] = useState("thisMonth");
  const [startDate, setStartDate] = useState(getStartOfMonth());
  const [endDate, setEndDate] = useState(getCurrentDate());
  const [productData, setProductData] = useState([]);
  const [currentLimit, setCurrentLimit] = useState(10);
  const [currentOffset, setCurrentOffset] = useState(0);

  // Orders section state
  const [orderActiveTab, setOrderActiveTab] = useState("orders");
  const [orderData, setOrderData] = useState([]);
  const [orderTableLoading, setOrderTableLoading] = useState(true);
  const [orderCurrentLimit, setOrderCurrentLimit] = useState(10);
  const [orderCurrentOffset, setOrderCurrentOffset] = useState(0);
  const [orderSearchQuery, setOrderSearchQuery] = useState("");
  const [orderIsSearching, setOrderIsSearching] = useState(false);

  const [vendorID, setVendorId] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [popup, setpopup] = useState(false);

  // Refs so debounced/async callbacks always read latest values
  const searchidRef = useRef(searchid);
  const startDateRef = useRef(startDate);
  const endDateRef = useRef(endDate);
  const productActiveTabRef = useRef(productActiveTab);
  const searchQueryRef = useRef(searchQuery);
  const searchByShortCodeRef = useRef(searchByShortCode);
  const orderActiveTabRef = useRef(orderActiveTab);
  const orderSearchQueryRef = useRef(orderSearchQuery);
  const orderCurrentLimitRef = useRef(orderCurrentLimit);

  const isGinne = useSelector((state) => state?.geneLoginReducer?.isGinneLogin);

  // Keep all refs in sync with state
  useEffect(() => { searchidRef.current = searchid; }, [searchid]);
  useEffect(() => { startDateRef.current = startDate; }, [startDate]);
  useEffect(() => { endDateRef.current = endDate; }, [endDate]);
  useEffect(() => { productActiveTabRef.current = productActiveTab; }, [productActiveTab]);
  useEffect(() => { searchQueryRef.current = searchQuery; }, [searchQuery]);
  useEffect(() => { searchByShortCodeRef.current = searchByShortCode; }, [searchByShortCode]);
  useEffect(() => { orderSearchQueryRef.current = orderSearchQuery; }, [orderSearchQuery]);
  useEffect(() => { orderCurrentLimitRef.current = orderCurrentLimit; }, [orderCurrentLimit]);

  const openOrderList = (id) => {
    navigate(`/vendorDetails?id=${id}`);
  };
  // ─── Orders API Call ──────────────────────────────────────────────────────
  const fetchOrderData = (
    search = "",
    paymentType = 0,
    offset = 0,
    start = startDateRef.current,
    end = endDateRef.current,
    vendorIdParam = searchidRef.current,
    limit = orderCurrentLimitRef.current,
  ) => {
    if (search) setOrderIsSearching(true);
    else setOrderTableLoading(true);

    const payload = {
      lang: i18next.language === "ar" ? "ar_001" : "en_US",
      user_id,
      state: toStateParam(paymentType),
      order_no: search,
      limit,
      offset,
      start_date: start || "",
      end_date: end || "",
      ...(vendorIdParam && { vendor_id: Number(vendorIdParam) }),
    };

    getPurchaseList(payload)
      .then((res) => {
        if (res?.result?.[0]?.status_code === 200) {
          setOrderData(res.result[0].response.map(flattenRow));
        } else {
          setOrderData([]);
        }
        setOrderIsSearching(false);
        setOrderTableLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching purchase list:", err);
        setOrderIsSearching(false);
        setOrderTableLoading(false);
      });
  };

  useEffect(() => {
  }, [])

  const openPurchaseOrder = async (orderNo) => {
          console.log("orderNo==>",orderNo);
     navigate(routes.purchase.purchaseOrder, {
            state: { order_no: orderNo },
          });
          
    // try {
    //   const payload = {
    //     lang: i18next.language === "ar" ? "ar_001" : "en_US",
    //     user_id,
    //     state: "",
    //     order_no: orderNo,
    //     limit: 10,
    //     offset: 0,
    //     start_date: "",
    //     end_date: "",
    //   };

    //   const res = await getPurchaseList(payload);

    //   if (res?.result?.[0]?.status_code === 200) {
    //     const rows = res.result[0].response.map(flattenRow);
    //     const match = rows.find((r) => r.order_no === orderNo) || rows[0];

    //     if (match?._raw) {
    //       navigate(routes.purchase.purchaseOrder, {
    //         state: { orderData: match._raw },
    //       });
    //     }
    //   }
    // } catch (err) {
    //   console.error("Error opening purchase order:", err);
    // }
  };
  const data = useMemo(
    () =>
      //   additionalData?.status
      //     ? additionalData?.all_product
      additionalData || [],
    [additionalData],
  );

  useEffect(() => {
    if (additionalData?.length === 0 && !flag) {
      const timer = setTimeout(() => {
        setflag(true);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [additionalData, flag]);

  const formatSessionState = (state) => {
    if (!state) return "";

    return state
      .split("_") // Split by underscore
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize the first letter of each word
      .join(" "); // Join the words with a space
  };

  const columns = useMemo(
    () => [
      {
        Header: (
          <div className="text-start justify-start" title={t("Date")}>
            {t("Date & Time")}
          </div>
        ),
        accessor: "date",
        headerClassName: "text-start",
        Cell: ({ row }) => (
          <div className="text-start justify-start">{row.original.date}</div>
        ),
      },
      {
        Header: (
          <div className="text-start justify-start" title={t("User")}>
            {t("User")}
          </div>
        ),
        minWidth: 200, // 👈 increase this
        width: 250, // 👈 preferred width
        maxWidth: 400, // 👈 optional limit
        accessor: "user_name",
        headerClassName: "text-start",
        Cell: ({ row }) => (
          <div className="text-start justify-start">
            {row.original.user_name}
          </div>
        ),
      },
      {
        Header: (
          <div
            className="text-start justify-start"
            title={t("Transaction Type")}
          >
            {t("Transaction Type")}
          </div>
        ),
        accessor: "expense_type",
        headerClassName: "text-start",
        Cell: ({ row }) => (
          <div className="text-start justify-start">
            {t(formatSessionState(row.original.expense_type))}
          </div>
        ),
      },
      // {
      //   Header: (
      //     <div className="text-start" title={t("Description")}>
      //       {t("Description")}
      //     </div>
      //   ),
      //   accessor: "remarks",
      //   Cell: ({ row }) => {
      //     const remarks = row.original.remarks || "";
      //     const truncated =
      //       remarks.length > 15 ? `${remarks.substring(0, 15)}...` : remarks;

      //     return (
      //       <div className="text-start">
      //         <Tooltip title={remarks}>
      //           <span className="">{truncated}</span>
      //         </Tooltip>
      //       </div>

      //     );
      //   },
      // },
      {
        Header: (
          <div className="text-start" title={t("Description")}>
            {t("Description")}
          </div>
        ),
        accessor: "remarks",
        Cell: ({ row }) => {
          const remarks = row.original.remarks || "";
          const translatedRemarks = t(remarks);
          const truncated =
            translatedRemarks.length > 15
              ? `${translatedRemarks.substring(0, 15)}...`
              : translatedRemarks;

          const isPurchasePayment = row.original.expense_type === "Purchase Payment";

          return (
            <div
              className={`text-start ${isPurchasePayment ? "cursor-pointer hover:underline text-primaryBlue" : ""}`}

              onClick={(e) => {
                if (isPurchasePayment) {
                  e.stopPropagation();
                  openPurchaseOrder(row.original.order_no || row.original.remarks); // not remarks
                }
              }}

            >
              <Tooltip title={translatedRemarks}>
                <span>{truncated}</span>
              </Tooltip>
            </div >
          );
        },
      },
      {
        Header: (
          <div className="text-start" title={t("Payment Method")}>
            {t("Payment Method")}
          </div>
        ),
        accessor: "payment_method",
        Cell: ({ row }) => (
          <div className="text-start">{t(row.original.payment_method)}</div>
        ),
      },
      {
        Header: (
          <div className="text-start" title={t("Amount")}>
            {t("Amount")}
          </div>
        ),
        accessor: "amount",
        Cell: ({ row }) => (
          <div className="text-start">
            {`EGP ${row.original.amount?.toFixed(2)}`}
          </div>
        ),
      },
      {
        Header: (
          <div className="text-start" title={t("Running Balance")}>
            {t("Running Balance")}
          </div>
        ),
        accessor: "balance",
        Cell: ({ row }) => (
          <div className="text-start">
            {`EGP ${row.original.balance?.toFixed(2)}`}
          </div>
        ),
      },
    ],
    ...(allStores
      ? [
        {
          Header: (
            <div className="text-start" title={t("Store Name")}>
              {t("Store Name")}
            </div>
          ),
          accessor: "store_name",
          Cell: ({ row }) => (
            <div className="text-start">{`${row.original.store_name}`}</div>
          ),
        },
      ]
      : []),
    [t],
  );

  const {
    headerGroups,
    prepareRow,
    page,
    nextPage,
    setPageSize,
    state: { pageSize },
  } = useTable(
    {
      columns,
      data: data,
      initialState: { pageSize: 10 },
    },
    useSortBy,
    usePagination,
    useRowSelect,
  );

  const [pageIndex, setpageIndex] = useState(1);

  useEffect(() => {
    const pageDetails = {
      limit: pageSize,
      offset: pageSize * (pageIndex - 1) < 0 ? 0 : pageSize * (pageIndex - 1),
    };

    if (pageSize) pagination(pageDetails);
  }, [pageSize, pageIndex]);

  return (
    <>
      <div
        className="container flex flex-col mx-auto mt-4"
        style={{ maxWidth: "100%" }}
      >
        {additionalData?.length == 0 && flag == false ? (
          <div className="h-screen w-[90%] mt-2 ">
            <ShimmerLoader />
          </div>
        ) : (
          <>
            {/* Header Table (Fixed) */}
            <div>
              <table
                className="font-normal  max-w-[100%] sm:w-[97.6%] md:w-[97.6%] lg:w-[97.6%] xl:w-[97.6%] overflow-hidden rounded-lg"
                style={{ tableLayout: "fixed" }}
              >
                <thead>
                  {headerGroups.map((headerGroup, headerIndex) => {
                    const { key: headerKey, ...headerGroupProps } =
                      headerGroup.getHeaderGroupProps();
                    return (
                      <tr
                        key={headerKey}
                        {...headerGroupProps}
                        className="bg-[#78CFEC66] text-sm font-semibold overflow-hidden whitespace-nowrap"
                      >
                        {headerGroup.headers.map((column, colIndex) => {
                          const { key: columnKey, ...columnProps } =
                            column.getHeaderProps(
                              column.getSortByToggleProps(),
                            );
                          return (
                            <th
                              key={columnKey}
                              {...columnProps}
                              className="py-3 whitespace-nowrap"
                              style={{ width: column.width }} // Set column width
                            >
                              <div className="flex flex-row items-center ms-2 text-sm leading-tight ">
                                {column.render("Header")}
                                {column.canSort && (
                                  <span className="ms-1">
                                    {column.isSorted ? (
                                      column.isSortedDesc ? (
                                        <img
                                          src={Vectorsorting}
                                          style={{
                                            minHeight: 10,
                                            minWidth: 10,
                                          }}
                                          alt="vector"
                                        />
                                      ) : (
                                        <img
                                          className="rotate-180"
                                          src={Vectorsorting}
                                          alt="vector"
                                        />
                                      )
                                    ) : (
                                      // 👇 Show a neutral icon when column is not sorted
                                      <img
                                        src={Vectorsorting}
                                        alt="sortable"
                                        style={{
                                          minHeight: 10,
                                          minWidth: 10,
                                          opacity: 0.3,
                                        }}
                                      />
                                    )}
                                  </span>
                                )}
                              </div>
                            </th>
                          );
                        })}
                      </tr>
                    );
                  })}
                </thead>
              </table>
            </div>

            {/* Body Table (Scrollable) */}
            <div
              className="scrollbar-none bg-[#F6F7F9]"
              style={{
                overflowY: "auto",
                maxHeight: "450px", // Adjust height as needed
              }}
            >
              <table
                className="font-normal max-w-[100%] sm:w-[97.6%]  md:w-[97.6%] lg:w-[97.6%] xl:w-[97.6%] overflow-hidden rounded-lg"
                style={{ tableLayout: "fixed" }}
              >
                <tbody className="bg-[#ffffff] text-center overflow-hidden">
                  {page.map((row) => {
                    prepareRow(row);
                    return (
                      <React.Fragment key={row.id}>
                        <tr
                          {...row.getRowProps()}
                          className="relative hover:bg-white cursor-pointer transition duration-300 border-b border-gray-300 fixed-row-height"
                        //   onClick={() => openOrderList(row.original.id)}
                        >
                          {row.cells.map((cell, index) => (
                            <td
                              key={index}
                              {...cell.getCellProps()}
                              className={`p-2 text-[#272728] h-1212 text-sm font-normal ${index < row.cells.length - 1 ? "relative" : ""
                                } ${cell?.column?.Header == "Product"
                                  ? "cursor-pointer"
                                  : ""
                                }`}
                              style={{ width: cell.column.width }} // Match column width
                            >
                              {cell.render("Cell")}
                            </td>
                          ))}
                        </tr>
                      </React.Fragment>
                    );
                  })}
                  {page.length === 0 && (
                    <tr>
                      <td colSpan={"100%"}>
                        <Empty
                          className="mt-10"
                          description={t("No data available")}
                        />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            <div className="">
              <CustomPagination
                data={data?.length}
                pageIndex={pageIndex}
                pageCount={page?.length}
                canPreviousPage={pageIndex != 1 ? true : false}
                canNextPage={data?.length == pageSize ? true : false}
                gotoPage={(page) => {
                  nextPage(page);
                }}
                previousPage={() => {
                  setpageIndex(pageIndex - 1);
                }}
                nextPage={() => {
                  setpageIndex(pageIndex + 1);
                }}
                pageSize={pageSize}
                setPageSize={(pageSize) => {
                  setpageIndex(1);
                  setPageSize(pageSize);
                }}
              />
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default ExpenseManagementTable;
